import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, authenticatedApi } from "../lib/api";
import { useAppState } from "../context/AppState";

let googleMapsPromise;

function loadGoogleMaps(key) {
  if (window.google?.maps) return Promise.resolve(window.google);
  if (!key) return Promise.reject(new Error("El mapa no está configurado."));
  if (!googleMapsPromise) {
    googleMapsPromise = new Promise((resolve, reject) => {
      const callback = `deliveryHuarazMapReady${Date.now()}`;
      window[callback] = () => {
        delete window[callback];
        resolve(window.google);
      };
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&callback=${callback}`;
      script.async = true;
      script.onerror = () => reject(new Error("No se pudo cargar Google Maps."));
      document.head.appendChild(script);
    });
  }
  return googleMapsPromise;
}

function ServiceMap({ apiKey, pickup, dropoff, activePoint, onPointChange, onRoute }) {
  const container = useRef(null);
  const map = useRef(null);
  const pickupMarker = useRef(null);
  const dropoffMarker = useRef(null);
  const directions = useRef(null);
  const activeRef = useRef(activePoint);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => { activeRef.current = activePoint; }, [activePoint]);

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps(apiKey)
      .then(({ maps }) => {
        if (!mounted || !container.current) return;
        map.current = new maps.Map(container.current, {
          center: { lat: pickup?.lat || -9.52779, lng: pickup?.lng || -77.52778 },
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        directions.current = new maps.DirectionsRenderer({
          map: map.current,
          suppressMarkers: true,
          polylineOptions: { strokeColor: "#f36b1c", strokeWeight: 5 },
        });
        setReady(true);
        map.current.addListener("click", (event) => {
          onPointChange(activeRef.current, {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
            address: "",
          }, true);
        });
      })
      .catch((requestError) => mounted && setError(requestError.message));
    return () => { mounted = false; };
  }, [apiKey]);

  useEffect(() => {
    if (!map.current || !window.google?.maps) return;
    const { maps } = window.google;
    const configure = (point, markerRef, label, color) => {
      if (!point?.lat || !point?.lng) {
        markerRef.current?.setMap(null);
        markerRef.current = null;
        return;
      }
      const position = { lat: Number(point.lat), lng: Number(point.lng) };
      if (!markerRef.current) {
        markerRef.current = new maps.Marker({
          map: map.current,
          position,
          draggable: true,
          label: { text: label, color: "#fff", fontWeight: "900" },
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 19,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 3,
          },
        });
        markerRef.current.addListener("dragend", (event) => {
          onPointChange(label === "A" ? "pickup" : "dropoff", {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
            address: "",
          }, true);
        });
      } else {
        markerRef.current.setPosition(position);
      }
    };
    configure(pickup, pickupMarker, "A", "#f36b1c");
    configure(dropoff, dropoffMarker, "B", "#3478d4");

    if (pickup?.lat && dropoff?.lat) {
      const bounds = new maps.LatLngBounds();
      bounds.extend({ lat: Number(pickup.lat), lng: Number(pickup.lng) });
      bounds.extend({ lat: Number(dropoff.lat), lng: Number(dropoff.lng) });
      map.current.fitBounds(bounds, 70);
      new maps.DirectionsService().route({
        origin: { lat: Number(pickup.lat), lng: Number(pickup.lng) },
        destination: { lat: Number(dropoff.lat), lng: Number(dropoff.lng) },
        travelMode: maps.TravelMode.DRIVING,
      }, (result, status) => {
        if (status === "OK") {
          directions.current.setDirections(result);
          const leg = result.routes?.[0]?.legs?.[0];
          onRoute({
            distanceKm: Number(leg?.distance?.value || 0) / 1000,
            durationMin: Math.ceil(Number(leg?.duration?.value || 0) / 60),
          });
        } else {
          directions.current.setDirections({ routes: [] });
          onRoute({ distanceKm: null, durationMin: null });
        }
      });
    } else if (pickup?.lat) {
      map.current.panTo({ lat: Number(pickup.lat), lng: Number(pickup.lng) });
    }
  }, [ready, pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng]);

  return <div className="service-map">{error ? <div><b>{error}</b><span>Puedes escribir las direcciones y continuar con las coordenadas.</span></div> : <div ref={container} />}</div>;
}

const initialContact = { name: "", phone: "" };

export default function ServiceEntry() {
  const { serviceId } = useParams();
  const isTaxi = serviceId === "taxi";
  const { address, user } = useAppState();
  const [catalogue, setCatalogue] = useState(null);
  const [pickup, setPickup] = useState(address ? { lat: Number(address.lat), lng: Number(address.lng), address: address.address } : null);
  const [dropoff, setDropoff] = useState(null);
  const [activePoint, setActivePoint] = useState(pickup ? "dropoff" : "pickup");
  const [route, setRoute] = useState({ distanceKm: null, durationMin: null });
  const [quote, setQuote] = useState(null);
  const [pickupInput, setPickupInput] = useState(address?.address || "");
  const [dropoffInput, setDropoffInput] = useState("");
  const [pickupContact, setPickupContact] = useState({ ...initialContact, name: user?.name || "", phone: user?.phone || "" });
  const [dropoffContact, setDropoffContact] = useState({ ...initialContact, name: user?.name || "", phone: user?.phone || "" });
  const [details, setDetails] = useState({
    pickupReference: "", dropoffReference: "", description: "", size: "SMALL",
    weight: "1", declaredValue: "", notes: "", passengerCount: "1",
    paymentMethod: "CASH", proposedFare: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api("/service-hub")
      .then(setCatalogue)
      .catch((requestError) => setError(requestError.message));
  }, []);

  const service = catalogue?.services?.find((entry) => entry.id === (isTaxi ? "TAXI" : "POINT_TO_POINT"));
  const config = isTaxi ? catalogue?.taxi : catalogue?.point_to_point;

  async function reverseGeocode(type, point) {
    try {
      const response = await api("/coordinate-to-address", {
        method: "POST",
        body: JSON.stringify({ lat: point.lat, lng: point.lng }),
      });
      const label = (typeof response === "string" ? response : response?.address) || `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;
      if (type === "pickup") {
        setPickup((current) => ({ ...current, address: label }));
        setPickupInput(label);
      } else {
        setDropoff((current) => ({ ...current, address: label }));
        setDropoffInput(label);
      }
    } catch {
      // Las coordenadas del mapa siguen siendo válidas.
    }
  }

  function changePoint(type, point, reverse = false) {
    const normalized = { ...point, lat: Number(point.lat), lng: Number(point.lng) };
    if (type === "pickup") setPickup(normalized);
    else setDropoff(normalized);
    setQuote(null);
    if (reverse) reverseGeocode(type, normalized);
  }

  function useGps() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      const point = { lat: coords.latitude, lng: coords.longitude, address: "" };
      changePoint("pickup", point, true);
      setActivePoint("dropoff");
    }, () => setError("No pudimos obtener tu ubicación. Revisa el permiso de GPS."));
  }

  async function locateAddress(type) {
    const query = type === "pickup" ? pickupInput : dropoffInput;
    if (!query.trim() || !catalogue?.maps_key) return;
    setBusy(true);
    try {
      const google = await loadGoogleMaps(catalogue.maps_key);
      new google.maps.Geocoder().geocode({ address: `${query}, Huaraz, Perú` }, (results, status) => {
        setBusy(false);
        if (status !== "OK" || !results?.[0]) {
          setError("No encontramos esa dirección en el mapa.");
          return;
        }
        const location = results[0].geometry.location;
        const point = { lat: location.lat(), lng: location.lng(), address: results[0].formatted_address };
        changePoint(type, point);
        if (type === "pickup") setPickupInput(point.address);
        else setDropoffInput(point.address);
      });
    } catch (requestError) {
      setBusy(false);
      setError(requestError.message);
    }
  }

  async function calculateQuote() {
    if (!pickup?.lat || !dropoff?.lat) {
      setError("Selecciona el punto A y el punto B en el mapa.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const response = await api(`/service-hub/${isTaxi ? "taxi" : "point-to-point"}/quote`, {
        method: "POST",
        body: JSON.stringify({
          pickup_latitude: pickup.lat,
          pickup_longitude: pickup.lng,
          dropoff_latitude: dropoff.lat,
          dropoff_longitude: dropoff.lng,
          route_distance_km: route.distanceKm,
          route_duration_min: route.durationMin,
        }),
      });
      setQuote(response.quote);
      if (isTaxi) setDetails((current) => ({ ...current, proposedFare: response.quote.total }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function createOrder(event) {
    event.preventDefault();
    if (!user?.auth_token) return;
    setBusy(true);
    setError("");
    try {
      const common = {
        pickup_address: pickupInput,
        pickup_latitude: pickup.lat,
        pickup_longitude: pickup.lng,
        dropoff_address: dropoffInput,
        dropoff_latitude: dropoff.lat,
        dropoff_longitude: dropoff.lng,
        route_distance_km: route.distanceKm,
        route_duration_min: route.durationMin,
        customer_notes: details.notes,
      };
      const payload = isTaxi ? {
        ...common,
        passenger_name: pickupContact.name,
        passenger_phone: pickupContact.phone,
        passenger_count: Number(details.passengerCount),
        payment_method: details.paymentMethod,
        proposed_fare: Number(details.proposedFare),
      } : {
        ...common,
        pickup_reference: details.pickupReference,
        pickup_contact_name: pickupContact.name,
        pickup_contact_phone: pickupContact.phone,
        dropoff_reference: details.dropoffReference,
        dropoff_contact_name: dropoffContact.name,
        dropoff_contact_phone: dropoffContact.phone,
        package_description: details.description,
        package_size: details.size,
        estimated_weight: Number(details.weight),
        declared_value: Number(details.declaredValue || 0),
        backpack_confirmation: true,
      };
      const response = await authenticatedApi(`/service-hub/${isTaxi ? "taxi" : "point-to-point"}/orders`, user.auth_token, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setSuccess(response.order);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  const updateDetails = (name, value) => setDetails((current) => ({ ...current, [name]: value }));
  const limits = catalogue?.point_to_point?.limits;

  return (
    <main className="service-request">
      <header className="service-request__header">
        <Link to="/inicio">‹</Link>
        <div><small>{isTaxi ? "MOVILIDAD" : "SERVICIO DE ENVÍO"}</small><h1>{service?.name || (isTaxi ? "Taxi" : "Recojo y entrega")}</h1></div>
      </header>
      {!catalogue && !error && <div className="food-message"><b>Cargando servicio…</b></div>}
      {service && !service.enabled && <div className="food-message food-message--error"><b>{service.unavailable_message}</b></div>}
      {error && <div className="food-message food-message--error"><b>{error}</b></div>}
      {success ? (
        <section className="service-success">
          <span>✓</span><h2>{isTaxi ? "Estamos buscando un conductor" : "Solicitud de envío creada"}</h2>
          <p>Referencia <b>{success.reference}</b>. Recibirás una notificación cuando un conductor acepte.</p>
          <Link to="/orders">Ver mis pedidos y servicios</Link>
        </section>
      ) : service?.enabled && (
        <>
          <div className="service-map-wrap">
            <ServiceMap
              apiKey={catalogue.maps_key}
              pickup={pickup}
              dropoff={dropoff}
              activePoint={activePoint}
              onPointChange={changePoint}
              onRoute={setRoute}
            />
            <div className="service-map-choice">
              <button className={activePoint === "pickup" ? "is-active" : ""} onClick={() => setActivePoint("pickup")} type="button">A · Recojo</button>
              <button className={activePoint === "dropoff" ? "is-active" : ""} onClick={() => setActivePoint("dropoff")} type="button">B · Entrega</button>
            </div>
          </div>
          <section className="service-route-form">
            <label><span><b>A</b> Punto de recojo</span><div><input value={pickupInput} onChange={(event) => setPickupInput(event.target.value)} placeholder="Escribe o marca en el mapa" /><button onClick={() => locateAddress("pickup")} type="button">Ubicar</button></div></label>
            <button className="service-gps" onClick={useGps} type="button">⌖ Usar mi GPS para el punto A</button>
            <label><span><b>B</b> Punto de entrega</span><div><input value={dropoffInput} onChange={(event) => setDropoffInput(event.target.value)} placeholder="Escribe o marca en el mapa" /><button onClick={() => locateAddress("dropoff")} type="button">Ubicar</button></div></label>
            <button className="service-quote-button" disabled={busy || !pickup || !dropoff} onClick={calculateQuote} type="button">{busy ? "Calculando…" : "Calcular ruta y tarifa"}</button>
          </section>
          {quote && (
            <form className="service-details-form" onSubmit={createOrder}>
              <section className="service-quote">
                <div><small>RUTA</small><b>{quote.distance_km} km · {quote.duration_min} min</b></div>
                <strong>S/ {Number(quote.total).toFixed(2)}<small>tarifa calculada</small></strong>
              </section>
              {!isTaxi ? (
                <>
                  <div className="service-form-grid">
                    <label>Contacto en recojo<input value={pickupContact.name} onChange={(event) => setPickupContact({ ...pickupContact, name: event.target.value })} required /></label>
                    <label>Teléfono de recojo<input value={pickupContact.phone} onChange={(event) => setPickupContact({ ...pickupContact, phone: event.target.value })} required /></label>
                    <label>Contacto en entrega<input value={dropoffContact.name} onChange={(event) => setDropoffContact({ ...dropoffContact, name: event.target.value })} required /></label>
                    <label>Teléfono de entrega<input value={dropoffContact.phone} onChange={(event) => setDropoffContact({ ...dropoffContact, phone: event.target.value })} required /></label>
                  </div>
                  <label>¿Qué vamos a llevar?<input value={details.description} onChange={(event) => updateDetails("description", event.target.value)} placeholder="Ej. documentos, cargador, paquete" required /></label>
                  <div className="service-form-grid service-form-grid--three">
                    <label>Tamaño<select value={details.size} onChange={(event) => updateDetails("size", event.target.value)}><option value="SMALL">Pequeño</option><option value="MEDIUM">Mediano</option><option value="LARGE">Grande</option></select></label>
                    <label>Peso aprox. (kg)<input type="number" min=".1" max={limits?.max_weight_kg} step=".1" value={details.weight} onChange={(event) => updateDetails("weight", event.target.value)} required /></label>
                    <label>Valor declarado<input type="number" min="0" step=".01" value={details.declaredValue} onChange={(event) => updateDetails("declaredValue", event.target.value)} /></label>
                  </div>
                  <div className="service-backpack"><span>🎒</span><div><b>Debe caber en la mochila</b><small>Máximo {limits?.max_dimensions_cm?.length} × {limits?.max_dimensions_cm?.width} × {limits?.max_dimensions_cm?.height} cm y {limits?.max_weight_kg} kg.</small></div></div>
                </>
              ) : (
                <>
                  <div className="service-form-grid">
                    <label>Pasajero<input value={pickupContact.name} onChange={(event) => setPickupContact({ ...pickupContact, name: event.target.value })} required /></label>
                    <label>Teléfono<input value={pickupContact.phone} onChange={(event) => setPickupContact({ ...pickupContact, phone: event.target.value })} required /></label>
                    <label>Pasajeros<select value={details.passengerCount} onChange={(event) => updateDetails("passengerCount", event.target.value)}><option>1</option><option>2</option><option>3</option><option>4</option></select></label>
                    <label>Pago<select value={details.paymentMethod} onChange={(event) => updateDetails("paymentMethod", event.target.value)}><option value="CASH">Efectivo</option><option value="YAPE">Yape</option><option value="PLIN">Plin</option></select></label>
                  </div>
                  <label>Tu oferta (S/)<input type="number" min="1" step=".50" value={details.proposedFare} onChange={(event) => updateDetails("proposedFare", event.target.value)} required /></label>
                </>
              )}
              <label>Indicaciones adicionales<textarea value={details.notes} onChange={(event) => updateDetails("notes", event.target.value)} maxLength={1000} /></label>
              {!user ? (
                <Link className="service-login" to={`/account?next=/services/${serviceId}`}>Inicia sesión para solicitar</Link>
              ) : (
                <button className="service-submit" disabled={busy} type="submit">{busy ? "Enviando…" : isTaxi ? "Buscar conductor" : "Solicitar recojo"}</button>
              )}
            </form>
          )}
        </>
      )}
    </main>
  );
}
